import { NgIf, NgFor, NgClass } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'NiFormRadio',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, FormsModule],
  templateUrl: './ni-radio.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NiRadioComponent), multi: true }]
})

export class NiRadioComponent implements ControlValueAccessor, OnChanges {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false; 
  @Input() verticalLayout = true;
  @Input() brandStyle = false;
  @Input() selectedValue: any;
  @Input() radioList: {
    id: string;
    value: any;
    label?: string;
    description?: string;
    disabled?: boolean;
    checked?: boolean;
  }[] = [];
  @Output() valueChange = new EventEmitter<any>();
  @Output() blur = new EventEmitter<void>();
  disabledInitially: boolean = this.disabled; 

  private onChange = (value: string) => {};
  private onTouched = () => {};
  private readonly generatedGroupName = `ni-radio-group-${Math.random().toString(36).slice(2, 9)}`;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['radioList']) {
      this.syncCheckedState();
    }
  }

  writeValue(value: string) {
    this.selectedValue = value;
    this.syncCheckedState();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  handleChange(event: Event) {
    const selectedValue = (event.target as HTMLInputElement).value;
    this.selectedValue = selectedValue;
    this.syncCheckedState();
    this.onChange(selectedValue);
    this.valueChange.emit(this.radioList.find(r => r.value === selectedValue));
  }

  handleBlur() {
    this.onTouched();
  }

  getRadioId(radio: { id: string }, index: number): string {
    const rawId = String(radio?.id ?? '').trim();
    const suffix = rawId || `option-${index}`;
    return `${this.generatedGroupName}-${suffix}`;
  }

  isSelected(radioValue: unknown, selectedValue: unknown): boolean {
    return String(radioValue) === String(selectedValue);
  }

  getRadioName(): string {
    const groupName = String(this.name ?? '').trim();
    if (!groupName) {
      return this.generatedGroupName;
    }

    return `${groupName}-${this.generatedGroupName}`;
  }

  trackByRadioValue(index: number, radio: { value: unknown; id?: string }): string {
    const rawId = String(radio?.id ?? '').trim();
    const value = String(radio?.value ?? '');
    return rawId || `${value}-${index}`;
  }

  private syncCheckedState() {
    this.radioList = this.radioList.map(radio => ({
      ...radio,
      checked: radio.value === this.selectedValue
    }));
  }

  enableRadio() {
    this.disabledInitially = false;
  }
  
  
}


interface RadioOption {
  id: string;
  label: string;
  value: string;
  checked?: boolean;
  disabled?: boolean;
  description?: string;
}